import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import "./Popup.scss";

const Popup = ({
	buttonContent,
	children,
	position,
	open,
	onToggle,
	className
}) => {
	const popupRef = useRef(null);

	const handleToggle = useCallback(() => {
		onToggle(!open);
	}, [open, onToggle]);

	const handleBackdropClick = useCallback(() => {
		onToggle(false);
	}, [onToggle]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (popupRef.current && !popupRef.current.contains(event.target)) {
				if (open) onToggle(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [open, onToggle]);

	return (
		<div
			className={classnames("popup", className, {
				"is-open": open
			})}
			ref={popupRef}
		>
			<div
				className="popup-trigger"
				onClick={handleToggle}
				onKeyDown={(e) => e.key === "Enter" && handleToggle()}
				role="button"
				tabIndex={0}
			>
				{buttonContent}
			</div>

			{open && (
				<>
					<div
						className="popup-backdrop"
						onClick={handleBackdropClick}
						onKeyDown={(e) => e.key === "Escape" && handleBackdropClick()}
						role="button"
						tabIndex={-1}
						aria-label="Close popup"
					/>
					<div className={classnames("popup-panel", `position-${position}`)}>
						{children}
					</div>
				</>
			)}
		</div>
	);
};

Popup.propTypes = {
	buttonContent: PropTypes.node.isRequired,
	children: PropTypes.node.isRequired,
	position: PropTypes.oneOf(["bottom-left", "bottom-right", "top-left", "top-right"]),
	open: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
	className: PropTypes.string
};

Popup.defaultProps = {
	position: "bottom-left",
	className: ""
};

export default Popup;
